import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import { PaymentStatus } from "src/business/payment/payment";
import { throwApiError } from "src/data/admin/apiError";
import { adminSubscriptionKeys } from "src/data/admin/queries/subscription";
import {
  AdminPaymentDto,
  ConfirmPaymentRequest,
  RejectPaymentRequest,
} from "src/data/admin/types/payment";

/** `null` is the unfiltered view: the `status` parameter is left off entirely. */
export type AdminPaymentStatusFilter = PaymentStatus | null;

export const adminPaymentKeys = {
  list: (status: AdminPaymentStatusFilter) =>
    ["admin", "payments", "list", status ?? "all"] as const,
};

const adminPaymentsUrl = `${API_URL}/admin/payments`;

// GET /web/admin/payments[?status=] — Admin only
// The filter is passed to the backend rather than applied client-side, and is
// part of the query key so each view caches independently.
export const useAdminPayments = (status: AdminPaymentStatusFilter) =>
  useQuery({
    queryKey: adminPaymentKeys.list(status),
    queryFn: async (): Promise<AdminPaymentDto[]> => {
      const query = status ? `?${new URLSearchParams({ status })}` : "";
      const response = await fetch(`${adminPaymentsUrl}${query}`, {
        credentials: "include",
      });
      if (!response.ok) {
        return throwApiError(response, "Failed to load payments");
      }
      return (await response.json()) as AdminPaymentDto[];
    },
  });

type DecisionCallbacks = {
  onSuccess?: (payment: AdminPaymentDto) => void;
};

/**
 * Confirming *is* the subscription change, so it invalidates the ops
 * subscription list and the restaurateur's own subscription card alongside the
 * queue. Every cached filter is dropped: the payment has left the pending view
 * and joined the confirmed one.
 */
const useQueueInvalidation = () => {
  const queryClient = useQueryClient();

  return (restaurantId?: string) => {
    queryClient.invalidateQueries({ queryKey: ["admin", "payments", "list"] });
    if (restaurantId) {
      queryClient.invalidateQueries({ queryKey: adminSubscriptionKeys.list() });
      queryClient.invalidateQueries({
        queryKey: ["subscription-tier", restaurantId],
      });
    }
  };
};

// POST /web/admin/payments/{paymentId}/confirm — Admin only
// Only the fields the admin actually changed are sent; an untouched dialog
// posts `{}`, which the backend reads as "keep every declared value".
export const useConfirmPayment = ({ onSuccess }: DecisionCallbacks = {}) => {
  const invalidate = useQueueInvalidation();

  return useMutation({
    mutationFn: async ({
      paymentId,
      overrides,
    }: {
      paymentId: string;
      overrides: ConfirmPaymentRequest;
    }): Promise<AdminPaymentDto> => {
      const response = await fetch(
        `${adminPaymentsUrl}/${paymentId}/confirm`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(overrides),
        },
      );
      if (!response.ok) {
        return throwApiError(response, "Failed to confirm the payment");
      }
      return (await response.json()) as AdminPaymentDto;
    },
    onSuccess: (payment) => {
      invalidate(payment.restaurantId);
      onSuccess?.(payment);
    },
  });
};

// POST /web/admin/payments/{paymentId}/reject — Admin only
// Rejection does not touch the subscription, so only the queue is invalidated.
export const useRejectPayment = ({ onSuccess }: DecisionCallbacks = {}) => {
  const invalidate = useQueueInvalidation();

  return useMutation({
    mutationFn: async ({
      paymentId,
      payload,
    }: {
      paymentId: string;
      payload: RejectPaymentRequest;
    }): Promise<AdminPaymentDto> => {
      const response = await fetch(`${adminPaymentsUrl}/${paymentId}/reject`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        return throwApiError(response, "Failed to reject the payment");
      }
      return (await response.json()) as AdminPaymentDto;
    },
    onSuccess: (payment) => {
      invalidate();
      onSuccess?.(payment);
    },
  });
};

/**
 * GET /web/admin/payments/{paymentId}/proof — Admin only
 *
 * Mirrors the staff helper: a credentialed fetch read as a blob, returning the
 * backend-resolved content type. No URL pointing at the stored document is ever
 * built or kept.
 */
export const downloadAdminProof = async (
  paymentId: string,
): Promise<{ blob: Blob; contentType: string | null }> => {
  const response = await fetch(`${adminPaymentsUrl}/${paymentId}/proof`, {
    credentials: "include",
  });
  if (!response.ok) {
    return throwApiError(response, "Failed to download the proof");
  }
  return {
    blob: await response.blob(),
    contentType: response.headers.get("Content-Type"),
  };
};
